/**
 * Form draft storage.
 *
 * Persists in-progress form input to durable device storage so that nothing the
 * user typed is lost when the app is backgrounded, reopened, or even killed by
 * the OS. Uses Capacitor Preferences on native (native UserDefaults /
 * SharedPreferences — survives app kills) and falls back to localStorage on web.
 *
 * All values are stored under a single prefix so drafts can be cleared in bulk
 * (e.g. on logout). Keys passed in by callers should already be scoped per user
 * (see useFormDraft) — this module only adds the shared prefix.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const DRAFT_PREFIX = 'santiyex-draft:';

const isNative = () => Capacitor.isNativePlatform();

/**
 * Build a per-user draft key. Keep this the single source of truth for key
 * scoping so every caller (the useFormDraft hook and any direct users) agrees.
 */
export const scopedDraftKey = (userId: string | undefined | null, formId: string): string =>
  `${userId || 'anon'}:${formId}`;

interface DraftEnvelope<T> {
  v: number; // schema version — bump to invalidate incompatible old drafts
  savedAt: number; // epoch ms, used for TTL
  data: T;
}

// JSON serialization that survives Date round-trips. Date.prototype.toJSON
// turns Dates into strings *before* a replacer runs, so we read the raw value
// off the holder (`this[key]`) to detect and tag real Date instances.
function serialize(data: unknown): string {
  return JSON.stringify(data, function (this: Record<string, unknown>, key: string, value: unknown) {
    const raw = this[key];
    if (raw instanceof Date) {
      return { __type: 'Date', value: raw.toISOString() };
    }
    return value;
  });
}

function deserialize<T>(raw: string): T | null {
  try {
    return JSON.parse(raw, (_key, value) => {
      if (
        value &&
        typeof value === 'object' &&
        (value as { __type?: string }).__type === 'Date' &&
        typeof (value as { value?: unknown }).value === 'string'
      ) {
        return new Date((value as { value: string }).value);
      }
      return value;
    }) as T;
  } catch {
    return null;
  }
}

async function readRaw(fullKey: string): Promise<string | null> {
  if (isNative()) {
    const { value } = await Preferences.get({ key: fullKey });
    return value;
  }
  try {
    return localStorage.getItem(fullKey);
  } catch {
    return null;
  }
}

async function writeRaw(fullKey: string, value: string): Promise<void> {
  if (isNative()) {
    await Preferences.set({ key: fullKey, value });
    return;
  }
  try {
    localStorage.setItem(fullKey, value);
  } catch {
    /* storage full / unavailable — drafts are best-effort */
  }
}

async function removeRaw(fullKey: string): Promise<void> {
  if (isNative()) {
    await Preferences.remove({ key: fullKey });
    return;
  }
  try {
    localStorage.removeItem(fullKey);
  } catch {
    /* ignore */
  }
}

/** Save a draft for `key`. Best-effort: never throws. */
export async function saveDraft<T>(key: string, data: T, version = 1): Promise<void> {
  try {
    const envelope: DraftEnvelope<T> = { v: version, savedAt: Date.now(), data };
    await writeRaw(DRAFT_PREFIX + key, serialize(envelope));
  } catch {
    /* best-effort */
  }
}

/**
 * Load a draft for `key`. Returns `null` when missing, corrupt, the wrong
 * version, or older than `maxAgeMs`. Stale/invalid drafts are pruned.
 */
export async function loadDraft<T>(
  key: string,
  opts: { version?: number; maxAgeMs?: number } = {},
): Promise<T | null> {
  try {
    const raw = await readRaw(DRAFT_PREFIX + key);
    if (!raw) return null;

    const envelope = deserialize<DraftEnvelope<T>>(raw);
    if (!envelope || typeof envelope !== 'object' || !('data' in envelope)) {
      await clearDraft(key);
      return null;
    }
    if (opts.version != null && envelope.v !== opts.version) {
      await clearDraft(key);
      return null;
    }
    if (opts.maxAgeMs != null && Date.now() - envelope.savedAt > opts.maxAgeMs) {
      await clearDraft(key);
      return null;
    }
    return envelope.data;
  } catch {
    return null;
  }
}

/** Remove a single draft. */
export async function clearDraft(key: string): Promise<void> {
  await removeRaw(DRAFT_PREFIX + key);
}

/** Remove every stored draft (e.g. on logout). */
export async function clearAllDrafts(): Promise<void> {
  try {
    if (isNative()) {
      const { keys } = await Preferences.keys();
      await Promise.all(
        keys.filter((k) => k.startsWith(DRAFT_PREFIX)).map((k) => Preferences.remove({ key: k })),
      );
      return;
    }
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(DRAFT_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* best-effort */
  }
}
